'use client'

import { Component, type ReactNode, useState } from 'react'
import { XCircle } from 'lucide-react'
import posthog from 'posthog-js'
import { useTranslations } from 'next-intl'

import { Button } from '@/components/ui/button'
import { Spinner } from '@/components/ui/spinner'
import {
  getErrorLocation,
  getRelevantFrames,
  parseErrorStack,
  formatStackFrame,
} from '@/lib/error-stack-parser'
import { createTraceId } from '@/lib/trace-context'
import { reloadAfterChunkError } from '@/lib/chunk-load-recovery'

interface ErrorBoundaryProps {
  children: ReactNode
  fallback?: ReactNode
}

interface ErrorBoundaryState {
  hasError: boolean
  error: Error | null
  errorInfo: React.ErrorInfo | null
  traceId: string | null
}

export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      traceId: null,
    }
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return {
      hasError: true,
      error,
      traceId: createTraceId(),
    }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    const traceId = this.state.traceId ?? createTraceId()
    console.error('[ErrorBoundary] Caught error:', { traceId }, error, errorInfo)
    this.setState({
      error,
      errorInfo,
      traceId,
    })

    // Parse error stack to extract source locations
    const errorLocation = getErrorLocation(error)
    const frames = parseErrorStack(error)
    const relevantFrames = getRelevantFrames(frames)
    const formattedFrames = relevantFrames.map(formatStackFrame)

    // Send error to PostHog with enhanced source location info
    try {
      if (
        typeof window !== 'undefined' &&
        process.env.NEXT_PUBLIC_POSTHOG_KEY
      ) {
        // Check if PostHog is initialized and loaded
        const posthogWithLoaded = posthog as typeof posthog & {
          __loaded?: boolean
        }
        const isPostHogLoaded =
          posthog &&
          typeof posthog.captureException === 'function' &&
          posthogWithLoaded.__loaded === true

        const errorProperties = {
          $exception_type: 'react_error_boundary',
          $exception_message: error.message,
          $exception_stack_trace_raw: error.stack,
          $exception_component_stack: errorInfo.componentStack,
          $exception_error_name: error.name,
          error_boundary: true,
          trace_id: traceId,
          digest: (error as Error & { digest?: string }).digest,
          // Enhanced source location information
          error_source_file: errorLocation.file,
          error_source_line: errorLocation.line,
          error_source_column: errorLocation.column,
          error_function_name: errorLocation.functionName,
          error_relevant_frames: formattedFrames.join('\n'),
          error_info: {
            componentStack: errorInfo.componentStack,
            sourceLocation: errorLocation,
            relevantFrames: formattedFrames,
          },
        }

        if (isPostHogLoaded) {
          posthog.captureException(error, errorProperties)
        } else {
          // PostHog not loaded yet, try to capture as regular event
          if (typeof posthog.capture === 'function') {
            posthog.capture('$exception', errorProperties)
          }
        }
      }
    } catch (posthogError) {
      console.error(
        '[ErrorBoundary] Failed to send error to PostHog:',
        posthogError,
      )
    }
    reloadAfterChunkError(error)
  }

  handleReload = () => {
    window.location.reload()
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback
      }

      return (
        <ErrorFallback
          traceId={this.state.traceId}
          onReload={this.handleReload}
        />
      )
    }

    return this.props.children
  }
}

interface ErrorFallbackProps {
  traceId: string | null
  onReload: () => void
}

function ErrorFallback({ traceId, onReload }: ErrorFallbackProps) {
  const t = useTranslations('Errors')
  const tCommon = useTranslations('Common')
  const [isReloading, setIsReloading] = useState(false)
  const handleReload = () => {
    setIsReloading(true)
    onReload()
  }

  return (
    <div className="flex min-h-screen items-center justify-center p-4">
      <div className="animate-in fade-in w-full max-w-md duration-300">
        <div className="border-destructive/20 bg-card rounded-[28px] border p-8 shadow-[0_20px_60px_-40px_rgba(15,23,42,0.45)]">
          {/* Error Icon */}
          <div className="animate-in zoom-in-50 mb-6 duration-300">
            <div className="bg-destructive/10 text-destructive flex size-12 items-center justify-center rounded-2xl">
              <XCircle className="size-6" />
            </div>
          </div>

          {/* Error Title */}
          <h1 className="text-foreground text-2xl font-semibold tracking-tight">
            {t('applicationError')}
          </h1>
          <p className="text-muted-foreground mt-2 text-sm">{t('genericMessage')}</p>

          {traceId && (
            <div className="border-border bg-muted/40 mt-4 rounded-lg border px-3 py-2">
              <p className="text-muted-foreground text-xs font-medium">
                {t('supportTraceId')}
              </p>
              <p className="text-foreground mt-1 break-all font-mono text-xs">
                {traceId}
              </p>
            </div>
          )}

          {/* Action Button */}
          <div className="mt-8">
            <Button
              onClick={handleReload}
              disabled={isReloading}
              className="w-full cursor-pointer"
            >
              {isReloading ? (
                <>
                  <Spinner className="me-2" />
                  {tCommon('reloading')}
                </>
              ) : (
                tCommon('reloadPage')
              )}
            </Button>
          </div>

          {/* Help Text */}
          <p className="text-muted-foreground mt-6 text-sm">
            {t('helpText')}
          </p>
        </div>
      </div>
    </div>
  )
}
